import { isNil, uniq } from "lodash";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import { ConstraintTypes } from "../../constants/constraints";
import { IdentifierDisplayRecord } from "../../types/immutableRecords/display/shared/IdentifierDisplayRecord";
import { SizeRecord } from "../../types/immutableRecords/helper/SizeRecord";
import { calcIdentifierSize, calcTableIdentifierSize } from "../../utils/calcTextSize";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import { typeSelectors } from "../definition/types";
import { itemSelectors } from "../items";

const getDisplayItemsById = state => state.display.displayItems;
const getItemDates = state => state.display.itemDates;
const getValidatedDependencies = state => state.display.validatedDependencies;
const getValidatedDateConstraints = state => state.display.validatedDateConstraints;
const getViewCardSizes = state => state.display.viewCardSizes;

/**
 * @param {TimelineRecord} state
 */
const getViolatedDependencies = createSelector(
  // inputs
  getValidatedDependencies,
  // calculate based on inputs
  dependencies => {
    return dependencies.filter(dep => dep.isViolated);
  }
);

/**
 * @param {TimelineRecord} state
 */
const getViolatedDateConstraints = createSelector(
  // inputs
  getValidatedDateConstraints,
  // calculate based on inputs
  constraints => {
    return constraints.filter(c => c.isViolated);
  }
);

// May choose to switch this to list selector if it is slow enough, but suspect it is a fast function so avoiding
// premature optimisation.
/**
 * @param {TimelineRecord} state
 */
const getValidatedRelativeConstraints = createSelector(
  // inputs
  getValidatedDateConstraints,
  // calculate based on inputs
  constraints => constraints.filter(c => c.constraintType === ConstraintTypes.Relative)
);

const extractItemIds = (result, dependencyOrConstraint) => {
  result.push(dependencyOrConstraint.subject);
  if (dependencyOrConstraint.object) {
    result.push(dependencyOrConstraint.object);
  }
  return result;
};

const getViolatedItems = createSelector(
  // inputs
  getViolatedDependencies,
  getViolatedDateConstraints,
  // calculate based on inputs
  (dependencies, constraints) => {
    const ids = [...dependencies.reduce(extractItemIds, []), ...constraints.reduce(extractItemIds, [])];
    return uniq(ids);
  }
);

const getIdLabel = (item, type, isAvatar) => {
  let label = itemSelectors.getCompactDisplayForItem(item, type);
  return isAvatar ? label : getLabelWithUntitled(label);
};

/**
 * @param {ItemRecord} item
 * @param {Map} typesById
 * @param {Map} colorMap
 * @param {number} fontSize
 * @param {Immutable.Map} widthMap
 */
const getIdentifierDisplayForItem = createCachedSelector(
  // inputs
  (item, _typesById, _colorMap) => item,
  (_item, typesById, _colorMap) => typesById,
  (_item, _typesById, colorMap) => colorMap,
  (_item, _typesById, _colorMap, fontSize) => fontSize,
  (_item, _typesById, _colorMap, _fontSize, widthMap) => widthMap,
  // output
  (item, typesById, colorMap, fontSize, widthMap) => {
    const type = typesById.get(item.type);
    const isAvatar = typeSelectors.isAvatarType(type);
    const label = getIdLabel(item, type, isAvatar);
    const hasFontInfo = !isNil(fontSize) && !isNil(widthMap);
    const tableCellSize = hasFontInfo ? calcTableIdentifierSize(label, isAvatar, fontSize, widthMap) : SizeRecord();
    const size = hasFontInfo ? calcIdentifierSize(label, isAvatar, fontSize, widthMap) : SizeRecord();
    return IdentifierDisplayRecord({
      id: item.id,
      isAvatar,
      label,
      image: null,
      color: itemSelectors.getItemSwatchColor(colorMap, item),
      tableCellSize,
      size,
      tooltip: item.label
    });
  }
)(item => item.id);

export default {
  getDisplayItemsById,
  getItemDates,
  getValidatedDependencies,
  getValidatedDateConstraints,
  getViewCardSizes,

  getViolatedDependencies,
  getViolatedDateConstraints,
  getViolatedItems,
  getValidatedRelativeConstraints,

  getIdentifierDisplayForItem
};
